import { VALTYPES, VALTYPE_ANY } from '../../../../data/interfaces';
import { categoricalOrderOptions } from '../../helpers/categoricalSorters';
import { createColorMappingAspect, createColorMappingConfigs } from '../../helpers/colorMappingInfo';
import {
  createConfigParamLabelSpace,
  createConfigParamMultipleChoice,
  createConfigParamTypeRange,
  createConfigParamBoolean,
} from '../../helpers/configParams';
import { TpChartManifest } from '../interfaces';
import { Stripplot } from './Stripplot';

export const manifestStripplot: TpChartManifest = {
  id: 'StripPlot',
  name: 'Strip plot',
  description: `Vizualises the **relationship** between a **categorical** and **numerical** property.
Each state of the categorical property defines another column, and the values of the numerical propertery are shown as data points on a vertical axis in that column.
In order to avoid overlap, random jitter is applied to the horizontal position of the data points inside the column.`,
  component: Stripplot,
  canvasBased: true,
  groups: [
    { groupId: 'categories', groupName: 'Categories' },
    { groupId: 'points', groupName: 'Points' },
    { groupId: 'pointColors', groupName: 'Point colors' },
  ],
  aspects: [
    {
      id: 'categories',
      name: 'Categories data',
      description: 'States of this property define the columns in the plot.',
      groupId: 'categories',
      required: true,
      quality: 1,
      dataType: VALTYPES.CATEGORICAL,
    },
    {
      id: 'values',
      name: 'Values data',
      groupId: 'points',
      description: 'Used as Y axis of each data point inside the column',
      required: true,
      quality: 1,
      dataType: [VALTYPES.NUMBER, VALTYPES.INTEGER],
    },
    {
      id: 'hoverLabel',
      name: 'Hover label',
      groupId: 'points',
      description: 'Text to be displayed when the mouse hovers over a data point.',
      required: false,
      dataType: VALTYPE_ANY,
    },
    createColorMappingAspect('color', 'Point color data', 'pointColors', 0.5, 'data points'),
  ],
  configParams: [
    {
      id: 'sortBy',
      name: 'Sort by',
      groupId: 'categories',
      description: 'How to sort the columns (states of the `categories` property).',
      paramType: createConfigParamMultipleChoice([
        ...categoricalOrderOptions,
        { id: 'avgValInc', name: 'Increasing average value' },
        { id: 'avgValDec', name: 'Decreasing average value' },
      ]),
    },
    {
      id: 'labelSpace',
      name: 'Label space',
      groupId: 'categories',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the different states.',
      paramType: createConfigParamLabelSpace(),
    },
    {
      id: 'emptyStrips',
      name: 'Show empty columns',
      groupId: 'categories',
      description: 'If set, columns for which no data is available are still shown in the plot.',
      paramType: createConfigParamBoolean(true),
    },
    {
      id: 'pointSizeFactor',
      name: 'Point size',
      groupId: 'points',
      description: 'Scaling factor for the overall size of the points in the plot.',
      paramType: createConfigParamTypeRange(0.2, 4, 0.1, 1),
    },
    {
      id: 'hideColorLegend',
      name: 'Hide color legend',
      description: 'If set, colors used for the data points are not represented in the legend.',
      groupId: 'categories',
      paramType: createConfigParamBoolean(false),
    },
    {
      id: 'opacity',
      name: 'Opacity',
      groupId: 'points',
      description: `Point opacity.
In case a plot contains many overlapping points, lowering the opacity (i.e. increasing transparency)
can help reveal the data structure.`,
      paramType: createConfigParamTypeRange(0.1, 1, 0.1, 0.7),
    },
    {
      id: 'unselectedOpacity',
      name: 'Unselected Opacity',
      groupId: 'points',
      description: `Unselected point opacity.`,
      paramType: createConfigParamTypeRange(0.01, 1, 0.1, 0.7),
    },
    ...createColorMappingConfigs('color', 'Color', 'pointColors'),
  ],

  examples: [
    {
      id: 'stripplot_1',
    },
    {
      id: 'stripplot_2',
    },
  ],
};
